import { formatIban, isValidIban } from './business-identity'
import { tt } from './i18n/translation-memory'
import { parseMoneyToCents, type MoneyInput } from './money'

/**
 * The SEPA EPC QR code a customer scans to pay an order.
 *
 * **FB-086 is in the data, not the code.** The stored payee IBAN is
 * `SK1400000000001285211111`, and it **fails mod-97** — its check digits are `14` where
 * the account number requires `55`. Every QR code this page has ever produced points at
 * an IBAN no bank will accept, and the page shows a green *QR platba je aktívna* banner
 * over it because it checks nothing at all.
 *
 * The payload itself is EPC069-12, which legacy builds correctly field for field. What it
 * does not do is check any of the standard's constraints: the IBAN's checksum, the BIC's
 * shape, the field lengths, or the **331-byte cap on the whole payload** — a limit that
 * Slovak text reaches sooner than it looks, because every diacritic is two bytes in UTF-8.
 *
 * **It lives in `packages/shared` because two surfaces need the same rules.** `/qr-payment`
 * configures the code and `/driving` shows one at a door; §5 forbids one module importing
 * another, and two copies of a standard is how the copies drift apart.
 */

export interface QrSettings {
  id?: number
  isActive?: boolean
  iban?: string
  swift?: string
  accountName?: string
  bankName?: string
  tbClientId?: string
  tbClientSecret?: string
  _hasTbClientSecret?: boolean
  tbAutoVerify?: boolean
  tbConnected?: boolean
  tbAccountId?: string | null
  tbAccountName?: string | null
  /** §9.402 - which kind of code the driver's sheet draws. */
  codeMode?: string | null
}

/**
 * Which of the two codes a door payment draws - FRONTEND.md §9.402, BACKEND.md §10A.177.
 *
 * - `bank` - the EPC069-12 SEPA payload, scannable in any banking app. §9.389's, and the default.
 * - `gateway` - the TatraPay session's own page, which §9.338 drew.
 *
 * **Anything unrecognised is `bank`**, which is the server's own fallback and the mode that needs
 * nothing from the gateway. Takes `unknown` on purpose: the settings response and the driver's
 * `qr-payment-data` both carry the field and neither is typed here, so the two screens cannot come
 * to disagree about what a franchise chose by spelling the rule twice.
 */
export type QrCodeMode = 'bank' | 'gateway'

export function codeModeOf(payload: unknown): QrCodeMode {
  const row = (typeof payload === 'object' && payload !== null ? payload : {}) as Record<
    string,
    unknown
  >
  return row.codeMode === 'gateway' ? 'gateway' : 'bank'
}

/** EPC069-12 field caps. */
export const MAX_NAME_LENGTH = 70
export const MAX_REMITTANCE_LENGTH = 140
/** The whole payload, in bytes — not characters. */
export const MAX_PAYLOAD_BYTES = 331

/**
 * The EPC payload, field for field as legacy builds it.
 *
 * ```
 * BCD            service tag
 * 002            version — a BIC is optional at 002 and required at 001
 * 1              character set: UTF-8
 * SCT            SEPA Credit Transfer
 * <BIC>
 * <name>         ≤ 70
 * <IBAN>
 * EUR0.00        amount
 * <purpose>      empty
 * <structured>   empty
 * <unstructured> ≤ 140 — the order number goes here
 * ```
 */
export function buildEpcPayload(
  settings: QrSettings,
  amount: MoneyInput,
  reference: string,
): string {
  const cents = parseMoneyToCents(amount)
  return [
    'BCD',
    '002',
    '1',
    'SCT',
    (settings.swift ?? '').trim().toUpperCase(),
    (settings.accountName ?? '').slice(0, MAX_NAME_LENGTH),
    (settings.iban ?? '').replace(/\s/g, '').toUpperCase(),
    // Money, so cents on the way in and two decimals on the way out (§14.7).
    //
    // A **decimal point**, and it is not negotiable: this string is the EPC payload a bank's QR reader
    // parses, not a figure anybody reads. A Slovak comma here would make the code unscannable — which is
    // also why the amount is built by hand and never by a formatter.
    `EUR${(cents / 100).toFixed(2)}`,
    '',
    '',
    reference.slice(0, MAX_REMITTANCE_LENGTH),
  ].join('\n')
}

/** UTF-8 bytes, because a Slovak diacritic is two of them and the cap is in bytes. */
export function payloadBytes(payload: string): number {
  return new TextEncoder().encode(payload).length
}

export type QrProblem =
  /** FB-086 — the account customers would be paying into. */
  | { kind: 'iban-checksum'; value: string }
  | { kind: 'no-iban' }
  | { kind: 'no-name' }
  | { kind: 'bic-shape'; value: string }
  | { kind: 'name-too-long'; length: number }
  | { kind: 'payload-too-long'; bytes: number }
  | { kind: 'amount-not-positive' }

/** A BIC is 8 or 11 characters: 4 bank, 2 country, 2 location, optional 3 branch. */
export function isValidBic(value: string | null | undefined): boolean {
  return /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test((value ?? '').replace(/\s/g, '').toUpperCase())
}

/**
 * Everything wrong with the configuration, worst first.
 *
 * A bad IBAN leads, because it is the only one that makes every code produced useless
 * while everything looks fine.
 */
export function qrProblems(settings: QrSettings): QrProblem[] {
  const problems: QrProblem[] = []

  const iban = (settings.iban ?? '').trim()
  if (iban === '') {
    problems.push({ kind: 'no-iban' })
  } else if (!isValidIban(iban)) {
    problems.push({ kind: 'iban-checksum', value: iban })
  }

  const name = (settings.accountName ?? '').trim()
  if (name === '') {
    problems.push({ kind: 'no-name' })
  } else if (name.length > MAX_NAME_LENGTH) {
    problems.push({ kind: 'name-too-long', length: name.length })
  }

  const bic = (settings.swift ?? '').trim()
  // Optional at EPC version 002, so only a *wrong* one is a problem.
  if (bic !== '' && !isValidBic(bic)) {
    problems.push({ kind: 'bic-shape', value: bic })
  }

  return problems
}

/** The problems with one generated code, on top of the configuration's. */
export function payloadProblems(
  settings: QrSettings,
  amount: MoneyInput,
  reference: string,
): QrProblem[] {
  const problems = [...qrProblems(settings)]
  if (parseMoneyToCents(amount) <= 0) problems.push({ kind: 'amount-not-positive' })

  const bytes = payloadBytes(buildEpcPayload(settings, amount, reference))
  if (bytes > MAX_PAYLOAD_BYTES) problems.push({ kind: 'payload-too-long', bytes })

  return problems
}

/**
 * Whether a code should be produced at all.
 *
 * A wrong IBAN, a missing one, a missing name, an over-long payload or a non-positive
 * amount all make the code unpayable — drawing one anyway is worse than drawing none,
 * because a customer will scan it.
 */
export function canRender(problems: QrProblem[]): boolean {
  return !problems.some(
    (problem) =>
      problem.kind === 'iban-checksum' ||
      problem.kind === 'no-iban' ||
      problem.kind === 'no-name' ||
      problem.kind === 'payload-too-long' ||
      problem.kind === 'amount-not-positive',
  )
}

/**
 * Whether the feature may be switched on.
 *
 * Legacy's banner reads *active* on `isActive && iban && accountName`, with no regard for
 * whether the IBAN is real — which is exactly how an invalid one stayed live.
 */
export function isReallyActive(settings: QrSettings): boolean {
  return settings.isActive === true && canRender(qrProblems(settings))
}

export interface QrForm {
  isActive: boolean
  iban: string
  swift: string
  accountName: string
  bankName: string
  tbClientId: string
  tbClientSecret: string
  tbAutoVerify: boolean
  /** §9.402 - `bank` or `gateway`, never null: the server answers one of the two words. */
  codeMode: QrCodeMode
}

export function formOf(settings: QrSettings | undefined): QrForm {
  return {
    isActive: settings?.isActive === true,
    iban: settings?.iban ?? '',
    swift: settings?.swift ?? '',
    accountName: settings?.accountName ?? '',
    bankName: settings?.bankName ?? '',
    tbClientId: settings?.tbClientId ?? '',
    tbClientSecret: '',
    tbAutoVerify: settings?.tbAutoVerify === true,
    codeMode: codeModeOf(settings),
  }
}

/**
 * The body `PUT /settings/qr-payment` wants.
 *
 * An untouched secret box sends nothing, so saving an unrelated field cannot clear a
 * stored credential — the same rule as the register's API key and the bank's client
 * secret.
 */
export function qrPayload(form: QrForm): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    isActive: form.isActive,
    // Stored unspaced, so one account is one string wherever it appears.
    iban: form.iban.replace(/\s/g, '').toUpperCase(),
    swift: form.swift.trim().toUpperCase(),
    accountName: form.accountName.trim(),
    bankName: form.bankName.trim(),
    tbClientId: form.tbClientId.trim(),
    tbAutoVerify: form.tbAutoVerify,
    // §9.402 - sent with the rest, on the same button. A switch that saved itself while the IBAN
    // above it waited for *Uložiť* is the disagreement §9.302 fixed on this very screen.
    codeMode: form.codeMode,
  }
  const secret = form.tbClientSecret.trim()
  if (secret) payload.tbClientSecret = secret
  return payload
}

/** `SK14 0000 0000 0012 8521 1111` — grouped as a bank prints it. */
export { formatIban }

/**
 * The check digits the stored account number actually requires.
 *
 * Shown beside a failing IBAN, because "the checksum is wrong" is a diagnosis and "it
 * should start SK55" is something somebody can act on. Computed the same way the
 * checksum is verified: the BBAN plus the country code plus `00`, mod 97, subtracted
 * from 98.
 */
export function expectedCheckDigits(iban: string): string | null {
  const clean = iban.replace(/\s/g, '').toUpperCase()
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(clean)) return null

  const rearranged = `${clean.slice(4)}${clean.slice(0, 2)}00`
  let remainder = 0
  for (const character of rearranged) {
    const digits = /\d/.test(character)
      ? character
      : String(character.charCodeAt(0) - 'A'.charCodeAt(0) + 10)
    for (const digit of digits) {
      remainder = (remainder * 10 + Number(digit)) % 97
    }
  }
  return String(98 - remainder).padStart(2, '0')
}

/** What a driver's phone would show as the payment note. */
export function referenceFor(orderNumber: string): string {
  return `${tt('Objednávka')} ${orderNumber}`.slice(0, MAX_REMITTANCE_LENGTH)
}
